<script setup>
import { ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ROUTE_NAMES } from "Admin/js/Constants/routes";
import KaamfuLogoIcon from "Admin/js/Components/Icons/KaamfuLogoIcon.vue";
import MessageCircleMore from "Admin/js/Components/Icons/MessageCircleMore.vue";
import WorkIcon from "Admin/js/Components/Icons/WorkIcon.vue";
import TeamIcon from "Admin/js/Components/Icons/TeamIcon.vue";
import ActivityIcon from "Admin/js/Components/Icons/ActivityIcon.vue";
import { useMobileUser } from "Admin/js/Mobile/Composables/useMobileUser";
import KProfileMobileMenu from "js/Admin/js/Components/Common/K/KProfileMobileMenu.vue";
import FooterChildWrapper from "Admin/js/Mobile/Components/Common/FooterChildWrapper.vue";

const { userProfile } = useMobileUser();
const route = useRoute();
const router = useRouter();

const activePanel = ref("align");
const unreadConversationsCount = ref(0);

const ALIGN_ROUTES = [
    ROUTE_NAMES.MOBILE_ALIGN,
    ROUTE_NAMES.MOBILE_CREATE_PERSONAL_PROFILE,
    ROUTE_NAMES.MOBILE_CREATE_ORGANIZATION,
    ROUTE_NAMES.MOBILE_INVITE_TEAM,
    ROUTE_NAMES.MOBILE_BUILD_PROJECTS,
    ROUTE_NAMES.MOBILE_KNOW_KAAMFU,
    ROUTE_NAMES.MOBILE_GET_HELP,
];
const ROUTE_TO_PANEL_MAP = {
    [ROUTE_NAMES.MOBILE_WORK]: "work",
    [ROUTE_NAMES.MOBILE_TEAM]: "team",
    [ROUTE_NAMES.MOBILE_ACTIVITY]: "activity",
    [ROUTE_NAMES.MOBILE_DIRECTORY]: "directory",
    [ROUTE_NAMES.MOBILE_REPORTS]: "reports",
};

const menuLinks = computed(() => [
    {
        label: "Work",
        panel: "work",
        route: ROUTE_NAMES.MOBILE_WORK,
        icon: WorkIcon,
        iconSize: 17,
        class: "work-main-menu-controls-tour-step",
    },
    {
        label: "Team",
        panel: "team",
        route: ROUTE_NAMES.MOBILE_TEAM,
        icon: TeamIcon,
        iconSize: 15,
        class: "team-main-menu-controls-tour-step",
    },
    {
        label: "Activity",
        panel: "activity",
        route: ROUTE_NAMES.MOBILE_ACTIVITY,
        icon: ActivityIcon,
        iconSize: 15,
        class: "activity-main-menu-controls-tour-step",
    },
    {
        label: "Directory",
        panel: "directory",
        route: ROUTE_NAMES.MOBILE_DIRECTORY,
        icon: "sym_r_group",
        iconSize: 18,
        class: "directory-main-menu-controls-tour-step",
    },
    {
        label: "Setup",
        panel: "align",
        route: ROUTE_NAMES.MOBILE_ALIGN,
        icon: "sym_o_checklist",
        iconSize: 18,
        class: "align-main-menu-controls-tour-step",
    },
    {
        label: "Reports",
        panel: "reports",
        route: ROUTE_NAMES.MOBILE_REPORTS,
        icon: "sym_r_finance",
        iconSize: 18,
        class: "reports-main-menu-controls-tour-step",
    },
]);

const activeMenuLink = computed(() => {
    const currentRoute = route.name;

    if (ALIGN_ROUTES.includes(currentRoute)) {
        return {
            label: "Setup",
            panel: "align",
            route: ROUTE_NAMES.MOBILE_ALIGN,
            icon: "sym_o_checklist",
            iconSize: 18,
        };
    }

    const found = menuLinks.value.find(
        (link) => link.panel === activePanel.value,
    );
    return (
        found || {
            label: "Setup",
            panel: "align",
            route: ROUTE_NAMES.MOBILE_ALIGN,
            icon: "sym_o_checklist",
            iconSize: 18,
        }
    );
});
const isDashboardActive = computed(() => {
    return route.name === ROUTE_NAMES.MOBILE_DASHBOARD;
});
const isConversationActive = computed(() => {
    return route.name === ROUTE_NAMES.MOBILE_CONVERSATION;
});
const isKaiActive = computed(() => {
    return route.name === ROUTE_NAMES.MOBILE_KAI;
});
const isMenuActive = computed(() => {
    return (
        ALIGN_ROUTES.includes(route.name) || !!ROUTE_TO_PANEL_MAP[route.name]
    );
});

function changePanel(panelConfig) {
    if (panelConfig.disabled) return;
    activePanel.value = panelConfig.panel;
    if (panelConfig.route) {
        router.push({ name: panelConfig.route });
    }
}

function navigateToConversation() {
    router.push({ name: ROUTE_NAMES.MOBILE_CONVERSATION });
}

function navigateToKai() {
    router.push({ name: ROUTE_NAMES.MOBILE_KAI });
}

function navigateToDashboard() {
    router.push({ name: ROUTE_NAMES.MOBILE_DASHBOARD });
}

function isActivePanel(panel) {
    if (panel === "align") {
        return ALIGN_ROUTES.includes(route.name);
    }
    return ROUTE_TO_PANEL_MAP[route.name] === panel;
}

watch(
    () => route.name,
    (newRouteName) => {
        if (ALIGN_ROUTES.includes(newRouteName)) {
            activePanel.value = "align";
            return;
        }

        const panelName = ROUTE_TO_PANEL_MAP[newRouteName];
        if (panelName) {
            const menuLink = menuLinks.value.find(
                (link) => link.panel === panelName,
            );
            if (menuLink && !menuLink.disabled) {
                activePanel.value = panelName;
            }
        }
    },
    { immediate: true },
);
</script>

<template>
    <footer
        class="mobile_footer d-flex align-stretch bg-section position-sticky"
    >
        <div
            class="d-flex align-center justify-center kaamfu-logo-button-wrapper dashboard-controls-tour-step"
            @click="navigateToDashboard"
        >
            <FooterChildWrapper :isActive="isDashboardActive">
                <div
                    class="rounded-circle kaamfu-logo-button bg-charcoalGrey d-flex align-center justify-center"
                >
                    <KaamfuLogoIcon :size="32" />
                </div>
            </FooterChildWrapper>
        </div>

        <div
            class="mobile_footer_menu d-flex align-center flex-grow-1 position-relative main-menu-actions-tour-step bg-section"
            @click="changePanel(activeMenuLink)"
        >
            <KProfileMobileMenu
                location="top start"
                :offset="[-10, 8]"
                min-width="180px"
                show-footer
            >
                <template #activator>
                    <FooterChildWrapper isPill :isActive="isMenuActive">
                        <button
                            class="mobile_header_pill d-flex align-center text-uppercase rounded-pill bg-section main-menu-actions"
                            :style="{
                                'border-color': isMenuActive
                                    ? 'transparent'
                                    : '',
                            }"
                        >
                            <KIconButton
                                noHover
                                iconColor="fontLight"
                                :icon="activeMenuLink.icon"
                                :size="25"
                            />

                            <span class="mobile_menu_text text-fontLight">
                                {{ activeMenuLink.label }}
                            </span>
                        </button>
                    </FooterChildWrapper>
                </template>

                <template #content>
                    <div class="mobile_footer_submenu bg-section">
                        <div class="d-flex flex-column">
                            <button
                                v-for="link in menuLinks"
                                :key="link.label"
                                class="mobile_footer_submenu_item d-flex align-center"
                                :class="[
                                    link.class,
                                    {
                                        'mobile_footer_submenu_item--active ':
                                            isActivePanel(link.panel),
                                        'mobile_footer_submenu_item--disabled':
                                            link.disabled,
                                    },
                                ]"
                                @click.stop="changePanel(link)"
                            >
                                <KIconButton
                                    noHover
                                    iconColor="fontLight"
                                    :icon="link.icon"
                                    :size="25"
                                    :iconSize="link.iconSize"
                                />
                                <span
                                    class="mobile_footer_submenu_text text-fontLight"
                                    :class="{
                                        'font-weight-bold': isActivePanel(
                                            link.panel,
                                        ),
                                    }"
                                >
                                    {{ link.label }}
                                </span>
                            </button>
                        </div>
                    </div>
                </template>
            </KProfileMobileMenu>
        </div>

        <div class="d-flex align-stretch">
            <div
                class="mobile_footer_btn mobile_footer_btn--kai d-flex align-center bg-section"
                @click="navigateToKai"
            >
                <FooterChildWrapper :isActive="isKaiActive">
                    <KProfile
                        size="l-medium"
                        class="cursor-pointer"
                        :image-src="userProfile?.agent?.avatar_url"
                        :name="userProfile?.agent?.first_name"
                    />
                </FooterChildWrapper>
            </div>

            <div
                class="mobile_footer_btn mobile_footer_btn--chat d-flex align-center position-relative bg-section"
                @click="navigateToConversation"
            >
                <FooterChildWrapper :isActive="isConversationActive">
                    <div
                        class="d-flex align-center justify-center position-relative chat-icon-wrapper rounded-circle bg-buttonBg"
                    >
                        <MessageCircleMore
                            :size="24"
                            class="text-fontLight"
                        />
                        <span
                            v-if="unreadConversationsCount > 0"
                            class="mobile_footer_badge d-flex align-center justify-center position-absolute bg-charcoalGrey text-fontDark"
                        >
                            {{
                                unreadConversationsCount > 9
                                    ? "9+"
                                    : unreadConversationsCount
                            }}
                        </span>
                    </div>
                </FooterChildWrapper>
            </div>
        </div>
    </footer>
</template>

<style lang="scss" scoped>
@import "../../../../sass/_theme-variables.sass";

.mobile_footer {
    min-height: 4rem;
    border-top: 1px solid $sectionBorder;
    bottom: 0;
    z-index: 100;

    &:has(.k_profile_menu) {
        z-index: 102;
    }
    .mobile_footer_btn {
        border: none;
        border-right: 1px solid $sectionBorder;
        transition: background-color 0.3s ease;
        cursor: pointer;
        padding: 0 0.75rem;

        &:hover {
            background-color: rgb(var(--v-theme-primary));
        }

        &--dashboard {
            min-width: 60px;
        }

        &--kai,
        &--chat {
            min-width: 64px;
            border-right: none;
            border-left: 1px solid $sectionBorder;
        }
    }
    .chat-icon-wrapper {
        width: 2.5rem;
        height: 2.5rem;
        .mobile_footer_badge {
            top: -5px;
            right: -5px;
            font-size: 10px;
            font-weight: 700;
            width: 18px;
            height: 18px;
            border-radius: 50%;
        }
    }

    .mobile_footer_menu {
        padding-left: 1rem;
        padding-right: 1rem;
        cursor: pointer;

        .mobile_header_pill {
            height: 2.5rem;
            border: 1px solid $sectionBorder;
            font-weight: 800;
            transition: all 0.3s ease;
            cursor: pointer;
            gap: 0.125rem;
            padding-right: 1rem;
            padding-top: 0.375rem;
            padding-bottom: 0.375rem;
            padding-left: 0.75rem;

            .mobile_menu_text {
                font-size: 10px;
                font-weight: 800;
                letter-spacing: 0.1em;
                line-height: 1;
                margin-left: 0.5rem;
                text-transform: uppercase;
            }
        }
    }

    .kaamfu-logo-button-wrapper {
        border-right: 1px solid rgb(var(--v-theme-sectionBorder));
        padding: 0 0.75rem;
        .kaamfu-logo-button {
            height: 2.5rem;
            width: 2.5rem;
        }

        &.disabled {
            opacity: 0.5;
            cursor: not-allowed;
            pointer-events: none;
        }
    }

    .menu_item--active {
        :deep(.menu_item_text) {
            font-weight: 700;
        }
    }

    .mobile_footer_submenu {
        min-width: 180px;

        .mobile_footer_submenu_item {
            height: 4rem;
            border: none;
            border-bottom: 1px solid $sectionBorder;
            cursor: pointer;
            transition: background-color 0.2s ease;
            text-align: left;
            white-space: nowrap;
            padding-right: 1.25rem;
            padding-left: 0.75rem;
            padding-top: 0.75rem;
            padding-bottom: 0.75rem;
            background-color: rgb(var(--v-theme-section));

            &:last-child {
                border-bottom: none;
            }

            &:hover {
                background-color: rgb(var(--v-theme-secondary));
            }

            &--active {
                .mobile_footer_submenu_text {
                    font-weight: 700;
                }
            }

            &--disabled {
                opacity: 0.5;
                cursor: not-allowed;
                pointer-events: none;

                &:hover {
                    background-color: rgb(var(--v-theme-section));
                }
            }

            .mobile_footer_submenu_text {
                font-size: 14px;
                font-weight: 400;
                margin-left: 0.5rem;
            }
        }
    }
}
</style>
